/**
 * Print-only copy helpers for the Monthly Consultation PDF (Canva redesign).
 * Does not change the monthly consultation bundle schema.
 */

import type { SiHuaKind } from "../../../utils/forecast/monthlyConsultation";

/**
 * Score-quality label used on scorecard rows and callouts.
 *
 * Thresholds (inclusive lower bound):
 * - Excellent: pct >= 85
 * - Growing: pct >= 70
 * - Stable: pct >= 55
 * - Needs Awareness: pct < 55, or non-finite input
 */
export type ScoreQualityLabel =
  | "Excellent"
  | "Growing"
  | "Needs Awareness"
  | "Stable";

/**
 * Map a 0–100 score percentage to a Canva-style quality label.
 *
 * @param pct - Score percentage (finite number expected)
 * @returns Quality label; "Needs Awareness" when pct is not finite
 */
export function scoreQualityLabel(pct: number): ScoreQualityLabel {
  if (typeof pct !== "number" || !Number.isFinite(pct)) {
    return "Needs Awareness";
  }
  if (pct >= 85) {
    return "Excellent";
  }
  if (pct >= 70) {
    return "Growing";
  }
  if (pct >= 55) {
    return "Stable";
  }
  return "Needs Awareness";
}

/**
 * Map a week-quality string (Push / Careful / Steady and variants) to a
 * FOCUS keyword line for print chrome.
 *
 * @param quality - Week quality from the bundle (e.g. "Best to push")
 * @returns Focus keyword string; default Push line when unmatched or empty
 */
export function weekFocusKeywords(quality: string): string {
  const raw = typeof quality === "string" ? quality.trim().toLowerCase() : "";
  if (raw.length === 0) {
    return "Clarity · Boundaries · Direction";
  }
  if (raw.includes("push") || raw.includes("best")) {
    return "Clarity · Boundaries · Direction";
  }
  if (raw.includes("careful") || raw.includes("prepare")) {
    return "Pace · Focus · Restraint";
  }
  if (raw.includes("steady")) {
    return "Rhythm · Follow-through · Consistency";
  }
  if (raw.includes("wrap")) {
    return "Close · Document · Prepare";
  }
  return "Clarity · Boundaries · Direction";
}

/**
 * Care / Rest / Move protocol slots for the Health chapter.
 */
export interface ProtocolBuckets {
  /** Care / daily-body slot. */
  readonly care: string;
  /** Rest / recovery slot. */
  readonly rest: string;
  /** Move / activity slot. */
  readonly move: string;
}

const PROTOCOL_FALLBACKS: ProtocolBuckets = {
  care: "Protect sleep and gentle daily care",
  rest: "Build in real recovery time",
  move: "Keep the body moving without strain",
};

/**
 * Classify a protocol line into care, rest, or move from keyword hints.
 *
 * @param line - Trimmed protocol sentence
 * @returns Bucket key, or null when unclassified
 */
function classifyProtocolLine(line: string): keyof ProtocolBuckets | null {
  const lower = line.toLowerCase();
  if (
    lower.includes("rest") ||
    lower.includes("sleep") ||
    lower.includes("recover") ||
    lower.includes("9pm") ||
    lower.includes("bedtime")
  ) {
    return "rest";
  }
  if (
    lower.includes("move") ||
    lower.includes("walk") ||
    lower.includes("travel") ||
    lower.includes("exercise") ||
    lower.includes("logistics")
  ) {
    return "move";
  }
  if (
    lower.includes("care") ||
    lower.includes("stretch") ||
    lower.includes("neck") ||
    lower.includes("shoulder") ||
    lower.includes("water") ||
    lower.includes("hydrat") ||
    lower.includes("drink")
  ) {
    return "care";
  }
  return null;
}

/**
 * Keyword-map protocol lines into Care / Rest / Move slots.
 * First matching line wins per bucket; leftover lines fill empty slots in
 * order; documented fallbacks fill any remaining gaps.
 *
 * @param protocol - Protocol sentences from the bundle
 * @returns Three non-empty slots
 */
export function protocolBuckets(
  protocol: readonly string[]
): ProtocolBuckets {
  const lines = Array.isArray(protocol)
    ? protocol
        .filter((item): item is string => typeof item === "string")
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
    : [];

  const assigned: { care: string | null; rest: string | null; move: string | null } = {
    care: null,
    rest: null,
    move: null,
  };
  const unused: string[] = [];

  for (const line of lines) {
    const bucket = classifyProtocolLine(line);
    if (bucket !== null && assigned[bucket] === null) {
      assigned[bucket] = line;
    } else {
      unused.push(line);
    }
  }

  const order: Array<keyof ProtocolBuckets> = ["care", "rest", "move"];
  for (const key of order) {
    if (assigned[key] === null && unused.length > 0) {
      const next = unused.shift();
      if (typeof next === "string" && next.length > 0) {
        assigned[key] = next;
      }
    }
  }

  return {
    care: assigned.care ?? PROTOCOL_FALLBACKS.care,
    rest: assigned.rest ?? PROTOCOL_FALLBACKS.rest,
    move: assigned.move ?? PROTOCOL_FALLBACKS.move,
  };
}

/**
 * Format a 1-based page index as `PAGE 01` (zero-padded to two digits).
 *
 * @param pageNumber - 1-based page index
 * @returns Uppercase page label; `PAGE 01` when the input is invalid
 */
export function paddedPageLabel(pageNumber: number): string {
  if (typeof pageNumber !== "number" || !Number.isFinite(pageNumber) || pageNumber < 1) {
    return "PAGE 01";
  }
  const n = Math.trunc(pageNumber);
  const digits = n < 100 ? String(n).padStart(2, "0") : String(n);
  return `PAGE ${digits}`;
}

/**
 * Build a compact report id from profile initials + solar year + lunar month.
 *
 * @param name - Profile display name
 * @param year - Solar year
 * @param month - Lunar month (1–12)
 * @returns Report id such as `KW-2026-M6`; `MC` prefix when the name has no letters
 */
export function buildReportId(name: string, year: number, month: number): string {
  const safeName = typeof name === "string" ? name.trim() : "";
  const initials = safeName
    .split(/\s+/)
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase())
    .join("")
    .slice(0, 4);
  const prefix = initials.length > 0 ? initials : "MC";
  const safeYear =
    typeof year === "number" && Number.isFinite(year) ? Math.trunc(year) : 0;
  const safeMonth =
    typeof month === "number" && Number.isFinite(month) ? Math.trunc(month) : 0;
  return `${prefix}-${String(safeYear)}-M${String(safeMonth)}`;
}

/**
 * Strip a trailing "Palace" word from an English palace label.
 *
 * @param name - Palace display name
 * @returns Name without a trailing Palace suffix
 */
export function stripPalaceSuffix(name: string): string {
  const raw = typeof name === "string" ? name.trim() : "";
  if (raw.length === 0) {
    return "";
  }
  return raw.replace(/ Palace$/i, "").trim();
}

/**
 * Cover / overview month line from the live bundle (never hardcoded sample dates).
 *
 * @param monthLabel - Bundle month label when present
 * @param lunarMonth - Lunar month number
 * @param solarYear - Solar year
 * @returns Uppercase `MONTH N, YYYY`
 */
export function monthPosterLabel(
  monthLabel: string,
  lunarMonth: number,
  solarYear: number
): string {
  const label = typeof monthLabel === "string" ? monthLabel.trim() : "";
  if (label.length > 0) {
    return label.toUpperCase();
  }
  const month =
    typeof lunarMonth === "number" && Number.isFinite(lunarMonth) && lunarMonth >= 1
      ? Math.trunc(lunarMonth)
      : 1;
  const year =
    typeof solarYear === "number" && Number.isFinite(solarYear)
      ? Math.trunc(solarYear)
      : 0;
  return `MONTH ${String(month)}, ${String(year)}`;
}

/**
 * Season-specific coaching sentence (product chrome keyed by `liuSeasonShort`).
 *
 * @param seasonShort - Growing / Harvest / Protecting / Rebuild
 * @returns One coaching sentence; empty when the season is unrecognized
 */
export function seasonCoachLine(seasonShort: string): string {
  const key = typeof seasonShort === "string" ? seasonShort.trim().toLowerCase() : "";
  if (key === "protecting") {
    return "Protect cash, reputation, and key relationships; postpone high-risk bets.";
  }
  if (key === "harvest") {
    return "Convert visibility into collected results; close loops rather than opening new fronts.";
  }
  if (key === "growing") {
    return "Start and expand the work that can compound; momentum comes from beginning, not polishing.";
  }
  if (key === "rebuild") {
    return "Rest, recover capacity, and reset the board before the next push.";
  }
  return "";
}

/**
 * Season coaching body: `liuSeasonPlain` plus a season-specific coach line.
 *
 * @param seasonPlain - Output of `liuSeasonPlain`
 * @param seasonShort - Output of `liuSeasonShort`
 * @returns One or two coaching sentences; empty when both inputs are blank
 */
export function seasonFocusBody(seasonPlain: string, seasonShort: string): string {
  const plain = typeof seasonPlain === "string" ? seasonPlain.trim() : "";
  const coach = seasonCoachLine(seasonShort);
  if (plain.length === 0) {
    return coach;
  }
  if (coach.length === 0) {
    return plain;
  }
  const stem = plain.replace(/\.$/, "");
  return `${stem}. ${coach}`;
}

/**
 * Three Overview season chips derived from the Liu month season — not per-client.
 *
 * @param seasonShort - Output of `liuSeasonShort`
 * @returns Exactly three tags, or an empty array when unrecognized
 */
export function seasonFocusChips(seasonShort: string): string[] {
  const key = typeof seasonShort === "string" ? seasonShort.trim().toLowerCase() : "";
  if (key === "protecting") {
    return ["Cash", "Reputation", "Stability"];
  }
  if (key === "harvest") {
    return ["Results", "Visibility", "Collection"];
  }
  if (key === "growing") {
    return ["Start", "Expand", "Momentum"];
  }
  if (key === "rebuild") {
    return ["Rest", "Prepare", "Reset"];
  }
  return [];
}

/**
 * True when an English palace label refers to the Parents palace.
 *
 * @param name - Natal or chart palace display name
 * @returns Whether the label contains a Parents palace token
 */
function isParentsPalaceName(name: string): boolean {
  const raw = typeof name === "string" ? name.trim().toLowerCase() : "";
  return raw.includes("parent");
}

/**
 * Palace coaching for the Overview core-palace card.
 * Parents palaces get a guidance/authority/mentor line; other palaces use
 * briefing guidance or area — never `primaryGoal`.
 *
 * @param natalPalace - Natal life-palace English name
 * @param chartPalace - Chart / flying life-palace English name
 * @param area - Briefing area label
 * @param keyAction - First non-empty `briefing.guidance.keyActions` item
 * @returns Short coaching sentence; empty when no source is available
 */
export function palaceFocusBody(
  natalPalace: string,
  chartPalace: string,
  area: string,
  keyAction: string
): string {
  if (isParentsPalaceName(natalPalace) || isParentsPalaceName(chartPalace)) {
    return "This palace puts guidance, authority, and mentor dynamics in the foreground — work with the people who set the rules, not around them.";
  }
  const action = typeof keyAction === "string" ? keyAction.trim() : "";
  if (action.length > 0) {
    return action;
  }
  const areaLabel = typeof area === "string" ? area.trim() : "";
  if (areaLabel.length > 0) {
    return `This month's palace focus sits in ${areaLabel}.`;
  }
  return "";
}

/**
 * Fallback three-tag sets keyed by stripped English palace name.
 * Used to pad Overview palace chips when `briefing.area` yields fewer than three.
 */
const PALACE_TAG_PAD: Record<string, readonly [string, string, string]> = {
  life: ["Self", "Identity", "Direction"],
  siblings: ["Peers", "Support", "Alliance"],
  spouse: ["Partnership", "Bond", "Dialogue"],
  children: ["Creative", "Projects", "Legacy"],
  wealth: ["Money", "Resources", "Flow"],
  health: ["Body", "Energy", "Pace"],
  travel: ["Movement", "Reach", "Change"],
  property: ["Home", "Assets", "Base"],
  friends: ["Network", "Circle", "Belonging"],
  career: ["Work", "Visibility", "Ownership"],
  wellbeing: ["Rest", "Inner", "Fuel"],
  parents: ["Guidance", "Authority", "Mentorship"],
};

/**
 * Map an English palace label to a `PALACE_TAG_PAD` key.
 *
 * @param name - Palace display name
 * @returns Map key, or empty string when unmatched
 */
function palaceTagPadKey(name: string): string {
  const short = stripPalaceSuffix(name).toLowerCase();
  if (short.includes("parent")) {
    return "parents";
  }
  if (short.includes("sibling")) {
    return "siblings";
  }
  if (short.includes("spouse") || short.includes("marriage") || short.includes("partner")) {
    return "spouse";
  }
  if (short.includes("child")) {
    return "children";
  }
  if (short.includes("wealth") || short.includes("money")) {
    return "wealth";
  }
  if (short.includes("health") || short.includes("body")) {
    return "health";
  }
  if (short.includes("travel") || short.includes("migration")) {
    return "travel";
  }
  if (short.includes("property") || short.includes("house") || short.includes("asset")) {
    return "property";
  }
  if (short.includes("friend") || short.includes("network")) {
    return "friends";
  }
  if (short.includes("career") || short.includes("official")) {
    return "career";
  }
  if (short.includes("wellbeing") || short.includes("fortune") || short.includes("mental")) {
    return "wellbeing";
  }
  if (short.includes("life")) {
    return "life";
  }
  return "";
}

/**
 * Three Overview palace chips from palace name + briefing area.
 * Parents always use Guidance / Authority / Mentorship.
 * Other palaces take comma-split area tokens, then pad from a palace-tag map.
 *
 * @param natalPalace - Natal life-palace English name
 * @param chartPalace - Chart / flying life-palace English name
 * @param area - Briefing area label (may be comma-separated)
 * @returns Up to three short tags
 */
export function palaceFocusTags(
  natalPalace: string,
  chartPalace: string,
  area: string
): string[] {
  if (isParentsPalaceName(natalPalace) || isParentsPalaceName(chartPalace)) {
    return ["Guidance", "Authority", "Mentorship"];
  }

  const tags: string[] = [];
  const seen = new Set<string>();
  const pushTag = (value: string): void => {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return;
    }
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    tags.push(trimmed);
  };

  const areaRaw = typeof area === "string" ? area.trim() : "";
  if (areaRaw.length > 0) {
    const parts = areaRaw.split(/[,/]/);
    for (const part of parts) {
      pushTag(part);
      if (tags.length >= 3) {
        return tags.slice(0, 3);
      }
    }
  }

  const padKeyRaw = palaceTagPadKey(
    typeof chartPalace === "string" && chartPalace.trim().length > 0
      ? chartPalace
      : natalPalace
  );
  const pad =
    padKeyRaw.length > 0 && Object.prototype.hasOwnProperty.call(PALACE_TAG_PAD, padKeyRaw)
      ? PALACE_TAG_PAD[padKeyRaw]
      : undefined;
  if (pad !== undefined) {
    for (const tag of pad) {
      pushTag(tag);
      if (tags.length >= 3) {
        break;
      }
    }
  }

  return tags.slice(0, 3);
}

/**
 * Canva product-chrome quote for the Overview executive reminder bar.
 * Not client data — identity already lives on the month-theme card.
 */
export const EXECUTIVE_REMINDER_QUOTE =
  "Small aligned actions compound over time.";

/**
 * Canva product-chrome labels for the three Overview reminder icons.
 * Target / Compass / TrendingUp pair with these strings in order.
 */
export const EXECUTIVE_REMINDER_POINTS = [
  "Own Your Patterns",
  "Lead With Clarity",
  "Create Long-Term Value",
] as const;

/**
 * Soft-clip a string at a word boundary for print density.
 *
 * @param text - Source copy
 * @param maxChars - Soft character cap
 * @returns Original text, or a clipped string ending in an ellipsis
 */
export function clipPdfText(text: string, maxChars: number): string {
  const trimmed = typeof text === "string" ? text.trim() : "";
  if (typeof maxChars !== "number" || !Number.isFinite(maxChars) || maxChars < 8) {
    return trimmed;
  }
  if (trimmed.length <= maxChars) {
    return trimmed;
  }
  const slice = trimmed.slice(0, maxChars);
  const lastSpace = slice.lastIndexOf(" ");
  const cut = lastSpace > 24 ? slice.slice(0, lastSpace) : slice;
  return `${cut.trim()}…`;
}

/**
 * Derive a short heading from the first clause of existing copy.
 * Does not invent astrology facts — only clips the source string.
 *
 * @param line - Source sentence
 * @param maxWords - Maximum words to keep in the heading
 * @returns Heading fragment, or empty string when the input is blank
 */
export function firstClauseHeading(line: string, maxWords = 5): string {
  const trimmed = typeof line === "string" ? line.trim() : "";
  if (trimmed.length === 0) {
    return "";
  }
  const limit =
    typeof maxWords === "number" && Number.isFinite(maxWords) && maxWords > 0
      ? Math.trunc(maxWords)
      : 5;
  const clause = trimmed.split(/[.!?;:—–]/)[0].trim();
  const source = clause.length > 0 ? clause : trimmed;
  const words = source.split(/\s+/).filter((word) => word.length > 0);
  if (words.length <= limit) {
    return source;
  }
  return words.slice(0, limit).join(" ");
}

/**
 * Title + body for a Focus "what to expect" card.
 */
export interface ExpectCardCopy {
  /** Short heading (2–4 words, or text before a dash/colon). */
  readonly title: string;
  /** Supporting sentence; full source line when the title was derived. */
  readonly body: string;
}

/**
 * Split a "what to expect" line into a short title and body.
 * Prefers text before an em/en dash or colon; otherwise a 2–4 word heading
 * from the first clause. Does not invent astrology facts.
 *
 * @param line - Signature-behavior sentence from the bundle
 * @returns Title plus body (body may be empty when the line is already a title)
 */
export function expectCardCopy(line: string): ExpectCardCopy {
  const trimmed = typeof line === "string" ? line.trim() : "";
  if (trimmed.length === 0) {
    return { title: "", body: "" };
  }

  const dashMatch = trimmed.match(/^(.{2,80}?)\s+[—–-]\s+(.+)$/);
  if (dashMatch !== null) {
    const head = typeof dashMatch[1] === "string" ? dashMatch[1].trim() : "";
    const tail = typeof dashMatch[2] === "string" ? dashMatch[2].trim() : "";
    if (head.length > 0 && tail.length > 0 && wordCount(head) <= 8) {
      return { title: head, body: tail };
    }
  }

  const colonIdx = trimmed.indexOf(":");
  if (colonIdx > 0 && colonIdx < trimmed.length - 1) {
    const head = trimmed.slice(0, colonIdx).trim();
    const tail = trimmed.slice(colonIdx + 1).trim();
    if (head.length > 0 && tail.length > 0 && wordCount(head) <= 8) {
      return { title: head, body: tail };
    }
  }

  const heading = firstClauseHeading(trimmed, 4);
  const sourceWords = trimmed.split(/\s+/).filter((word) => word.length > 0);
  let title = heading;
  if (wordCount(title) < 2 && sourceWords.length >= 2) {
    title = sourceWords.slice(0, 2).join(" ");
  }
  if (title.length === 0) {
    return { title: trimmed, body: "" };
  }
  if (title === trimmed || wordCount(trimmed) <= 4) {
    return { title, body: "" };
  }
  return { title, body: trimmed };
}

/**
 * Split a behavior / expect line into a short title and optional remainder.
 * Delegates to `expectCardCopy` so Focus cards and any older callers stay aligned.
 *
 * @param line - Signature-behavior sentence from the bundle
 * @returns Title plus optional body; body is null when empty
 */
export function titledBehaviorLine(
  line: string
): { title: string; body: string | null } {
  const copy = expectCardCopy(line);
  return {
    title: copy.title,
    body: copy.body.length > 0 ? copy.body : null,
  };
}

/**
 * Count whitespace-separated words in a string.
 *
 * @param text - Source text
 * @returns Word count (0 when blank)
 */
function wordCount(text: string): number {
  if (typeof text !== "string" || text.trim().length === 0) {
    return 0;
  }
  return text.trim().split(/\s+/).filter((word) => word.length > 0).length;
}

/**
 * Merge usual-ask lines and action steps into a short "window to" list.
 * Deduplicates by trimmed text; does not invent extra items.
 *
 * @param usualAskLines - Rarity usual-ask bullets
 * @param actionSteps - Rarity action steps
 * @param limit - Maximum rows to return (default 3)
 * @returns Up to `limit` non-empty unique strings
 */
export function windowToLines(
  usualAskLines: readonly string[],
  actionSteps: readonly string[],
  limit = 3
): string[] {
  const cap =
    typeof limit === "number" && Number.isFinite(limit) && limit > 0
      ? Math.trunc(limit)
      : 3;
  const seen = new Set<string>();
  const out: string[] = [];
  const pools: ReadonlyArray<readonly string[]> = [
    Array.isArray(usualAskLines) ? usualAskLines : [],
    Array.isArray(actionSteps) ? actionSteps : [],
  ];

  for (const pool of pools) {
    for (const item of pool) {
      if (typeof item !== "string") {
        continue;
      }
      const trimmed = item.trim();
      if (trimmed.length === 0 || seen.has(trimmed)) {
        continue;
      }
      seen.add(trimmed);
      out.push(trimmed);
      if (out.length >= cap) {
        return out;
      }
    }
  }
  return out;
}

/**
 * Executive-reminder copy derived from identity / headline / priority.
 * Does not add bundle fields or hardcoded sample slogans.
 */
export interface ExecutiveReminderCopy {
  /** Quote line (identity, else headline, else priority). */
  readonly quote: string;
  /** Up to three short labels from the same source fields. */
  readonly points: string[];
}

/**
 * Build overview executive-reminder quote and scannable points.
 *
 * @param identityLine - Archetype identity line
 * @param headline - Bundle headline
 * @param priority - Briefing priority
 * @returns Quote plus up to three derived points (may be empty)
 */
export function executiveReminderCopy(
  identityLine: string,
  headline: string,
  priority: string
): ExecutiveReminderCopy {
  const identity = typeof identityLine === "string" ? identityLine.trim() : "";
  const head = typeof headline === "string" ? headline.trim() : "";
  const prior = typeof priority === "string" ? priority.trim() : "";

  const quote =
    identity.length > 0 ? identity : head.length > 0 ? head : prior;

  const points: string[] = [];
  const pushUnique = (value: string): void => {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return;
    }
    const key = trimmed.toLowerCase();
    if (points.some((item) => item.toLowerCase() === key)) {
      return;
    }
    points.push(trimmed);
  };

  if (prior.length > 0) {
    pushUnique(prior);
  }
  const headLabel = firstClauseHeading(head, 4);
  if (headLabel.length > 0) {
    pushUnique(headLabel);
  }
  const identityLabel = firstClauseHeading(identity, 4);
  if (identityLabel.length > 0) {
    pushUnique(identityLabel);
  }

  return { quote, points: points.slice(0, 3) };
}

/**
 * Focus-chapter step-forward mantra from live bundle fields.
 */
export interface FocusMantraCopy {
  /** Primary mantra heading. */
  readonly heading: string;
  /** Supporting sentence; empty when no distinct second field exists. */
  readonly body: string;
}

/**
 * Compose the Focus mantra heading and body without sample-client copy.
 * Prefers identity line, then primary goal, then headline; body is the next
 * distinct field (goal, usual ask, or headline).
 *
 * @param input - Live headline, identity, goal, and usual-ask fields
 * @returns Heading and optional body
 */
export function focusMantraCopy(input: {
  headline: string;
  identityLine: string;
  primaryGoal: string;
  usualAsk: string;
}): FocusMantraCopy {
  const headline =
    typeof input.headline === "string" ? input.headline.trim() : "";
  const identity =
    typeof input.identityLine === "string" ? input.identityLine.trim() : "";
  const goal =
    typeof input.primaryGoal === "string" ? input.primaryGoal.trim() : "";
  const ask = typeof input.usualAsk === "string" ? input.usualAsk.trim() : "";

  const heading =
    identity.length > 0
      ? identity
      : goal.length > 0
        ? goal
        : headline;

  let body = "";
  if (heading === identity) {
    body = goal.length > 0 ? goal : ask.length > 0 ? ask : headline;
  } else if (heading === goal) {
    body = ask.length > 0 ? ask : headline;
  } else {
    body = ask;
  }

  if (body === heading) {
    body = "";
  }

  return { heading, body };
}

/**
 * Dark executive-insight line for the Focus chapter.
 *
 * @param priority - Briefing priority
 * @param headline - Bundle headline fallback
 * @returns Insight sentence, or empty when both fields are blank
 */
export function executiveInsightLine(priority: string, headline: string): string {
  const prior = typeof priority === "string" ? priority.trim() : "";
  if (prior.length > 0) {
    const cue = prior.replace(/\.$/, "");
    return `This is your cue to ${cue.toLowerCase()}.`;
  }
  const head = typeof headline === "string" ? headline.trim() : "";
  return head;
}

/**
 * Print role for one of the four Si Hua transformations.
 * lead = 化忌, support = 化权, recognition = 化科, resource = 化禄.
 */
export type SiHuaPrintRole = "lead" | "support" | "recognition" | "resource";

/** Canonical 2×2 card order matching Canva Signals & Activations. */
export const SI_HUA_PRINT_ROLES: readonly SiHuaPrintRole[] = [
  "lead",
  "support",
  "recognition",
  "resource",
];

const ROLE_TO_KIND: Record<SiHuaPrintRole, SiHuaKind> = {
  lead: "化忌",
  support: "化权",
  recognition: "化科",
  resource: "化禄",
};

/**
 * Map a Si Hua kind onto the Canva print role.
 *
 * @param kind - 禄 / 权 / 科 / 忌
 * @returns Print role used by PdfSiHuaCard
 */
export function siHuaRoleFromKind(kind: SiHuaKind): SiHuaPrintRole {
  if (kind === "化忌") {
    return "lead";
  }
  if (kind === "化权") {
    return "support";
  }
  if (kind === "化科") {
    return "recognition";
  }
  return "resource";
}

/**
 * Map a print role back to its Si Hua kind.
 *
 * @param role - Canva 2×2 role
 * @returns Matching 禄 / 权 / 科 / 忌
 */
export function siHuaKindFromRole(role: SiHuaPrintRole): SiHuaKind {
  return ROLE_TO_KIND[role];
}

/**
 * English romanization → Chinese star characters (simplified preferred).
 * Inverse of `starToEnglish` / `STAR_NAME_TO_ENGLISH` plus traditional extras.
 */
const ENGLISH_STAR_TO_CHINESE: Record<string, string> = {
  "zi wei": "紫微",
  "po jun": "破军",
  "tian fu": "天府",
  "lian zhen": "廉贞",
  "tai yin": "太阴",
  "tan lang": "贪狼",
  "ju men": "巨门",
  "tian tong": "天同",
  "tian xiang": "天相",
  "wu qu": "武曲",
  "tian liang": "天梁",
  "tai yang": "太阳",
  "qi sha": "七杀",
  "tian ji": "天机",
  "zuo fu": "左辅",
  "you bi": "右弼",
  "wen chang": "文昌",
  "wen qu": "文曲",
  "tian chu": "天厨",
  "ling xing": "铃星",
};

/**
 * Resolve Chinese star characters from a live activation star name.
 * Uses the Chinese field when present; otherwise inverts the English star table.
 *
 * @param starName - Chinese or English star name from the bundle
 * @returns Chinese characters, or empty when unknown
 */
export function chineseStarFromName(starName: string): string {
  const raw = typeof starName === "string" ? starName.trim() : "";
  if (raw.length === 0) {
    return "";
  }
  if (/[\u4e00-\u9fff]/.test(raw)) {
    return raw;
  }
  const mapped = ENGLISH_STAR_TO_CHINESE[raw.toLowerCase()];
  return typeof mapped === "string" ? mapped : "";
}

/**
 * Format the English star heading used on a Si Hua print card, e.g. `Ju Men (Ji)`.
 *
 * @param starEnglish - Romanized star name
 * @param kindLabel - Lu / Quan / Ke / Ji
 * @returns Combined heading; empty when both parts are blank
 */
export function siHuaStarHeading(starEnglish: string, kindLabel: string): string {
  const star = typeof starEnglish === "string" ? starEnglish.trim() : "";
  const kind = typeof kindLabel === "string" ? kindLabel.trim() : "";
  if (star.length === 0) {
    return kind.length > 0 ? `(${kind})` : "";
  }
  if (kind.length === 0) {
    return star;
  }
  return `${star} (${kind})`;
}

/**
 * Prefix an activation move with `Action:` unless it already starts that way.
 *
 * @param move - Live activation move line
 * @returns Prefixed action, or empty when the source is blank
 */
export function siHuaActionLine(move: string): string {
  const trimmed = typeof move === "string" ? move.trim() : "";
  if (trimmed.length === 0) {
    return "";
  }
  if (/^action\b/i.test(trimmed)) {
    return trimmed;
  }
  return `Action: ${trimmed}`;
}

/**
 * Canva page-04 static intro under "Signals & Activations".
 * Product chrome — not derived from briefing priority or area.
 */
export const ACTIVATIONS_INTRO =
  "This is your monthly guide to decode energies, respond with clarity, and move in rhythm with what matters most.";

/**
 * Page intro for Stars in the activated palace (extra PDF sheet).
 * Product chrome — explains what the page is for.
 */
export const STARS_PAGE_INTRO =
  "This page names the main stars sitting in this month's focus palace — the stage timing is lighting up. Each star is a tone or method cue: how you tend to show up when this life area is active. Pair these cues with last page's Lu / Quan / Ke / Ji moves.";

/** Short definition of natal vs borrowed vs open focus stars. */
export const STARS_WHAT_THIS_MEANS =
  "Natal stars live in the palace permanently. Borrowed stars come from the opposite palace when the focus palace has no main stars. An open palace has no main stars on either side — month activations lead more than fixed star personality.";

/** Section blurb under Star cues. */
export const STARS_CUES_BLURB =
  "Read each cue as this month's working style in the activated palace. Lead with the first star before mid-month; treat the rest as supporting color.";

/** Section blurb under Natal transforms. */
export const STARS_TRANSFORMS_BLURB =
  "These are birth-chart Four Transformations that already sit in this palace. They tint how month timing lands here — Lu eases flow, Quan adds control, Ke draws notice, Ji flags friction.";

/** Section blurb under Month signals in this palace. */
export const STARS_SIGNALS_BLURB =
  "Stem activations landing in this same palace this month. Use them as concrete moves that amplify or pressure the star cues above.";

/**
 * Intro line for Signals & Activations (Canva static copy).
 * Briefing fields are ignored so the PDF matches the redesign template.
 *
 * @param _priority - Unused; kept for call-site compatibility
 * @param _area - Unused; kept for call-site compatibility
 * @returns Canva static intro sentence
 */
export function activationsIntroLine(_priority: string, _area: string): string {
  return ACTIVATIONS_INTRO;
}

/**
 * Mode-specific explainer for the Stars focus-source card.
 *
 * @param mode - natal | borrowed | open
 * @param focusEnglish - Focus palace English name
 * @param oppositeEnglish - Opposite palace when borrowed, else null
 * @returns Coaching paragraph
 */
export function starsModeExplainer(
  mode: "natal" | "borrowed" | "open",
  focusEnglish: string,
  oppositeEnglish: string | null
): string {
  const focus =
    typeof focusEnglish === "string" && focusEnglish.trim().length > 0
      ? focusEnglish.trim()
      : "this focus palace";
  const opposite =
    typeof oppositeEnglish === "string" && oppositeEnglish.trim().length > 0
      ? oppositeEnglish.trim()
      : null;

  if (mode === "borrowed" && opposite !== null) {
    return [
      `No natal main stars sit in ${focus}.`,
      `Zi Wei Dou Shu borrows the opposite palace (${opposite}) so you still have star cues for this month's stage.`,
      "Borrowed tags mark the source — the coaching still applies to the focus area above.",
    ].join(" ");
  }
  if (mode === "open") {
    return [
      `No natal main stars sit in ${focus}, and the opposite palace is also open.`,
      "Less fixed personality pressure here — lean on month activations (Lu / Quan / Ke / Ji) and the teaching notes below.",
    ].join(" ");
  }
  return [
    `Your natal main stars in ${focus} set the default tone for this month's focus stage.`,
    "They do not replace the month activations; they describe how you tend to work this room when timing lights it up.",
  ].join(" ");
}

/**
 * One trap sentence from description and/or exit move (no invented copy).
 *
 * @param description - Failure-mode description
 * @param exitMove - Failure-mode exit move
 * @returns Combined sentence, or empty when both sources are blank
 */
export function monthTrapBody(description: string, exitMove: string): string {
  const descRaw = typeof description === "string" ? description.trim() : "";
  const exit = typeof exitMove === "string" ? exitMove.trim() : "";
  const desc = descRaw.length > 0 ? firstSentence(descRaw) : "";
  if (desc.length > 0 && exit.length > 0) {
    const stem = desc.replace(/\.$/, "");
    return `${stem}. Exit: ${exit}`;
  }
  if (desc.length > 0) {
    return desc;
  }
  return exit;
}

/**
 * First sentence of a paragraph (period, question, or exclamation).
 *
 * @param text - Source copy
 * @returns First sentence, or the full trimmed string when no terminator exists
 */
function firstSentence(text: string): string {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return "";
  }
  const match = trimmed.match(/^(.+?[.!?])(\s|$)/);
  if (match !== null && typeof match[1] === "string" && match[1].trim().length > 0) {
    return match[1].trim();
  }
  return trimmed;
}

/**
 * Compact week label for the activations teaser (`Week 1` from a longer window).
 *
 * @param window - Week-plan window string
 * @returns Short week label
 */
export function weekTeaserLabel(window: string): string {
  const raw = typeof window === "string" ? window.trim() : "";
  if (raw.length === 0) {
    return "Week";
  }
  const match = raw.match(/^(week\s+\d+)/i);
  if (match !== null && typeof match[1] === "string" && match[1].length > 0) {
    return match[1];
  }
  return raw;
}

/**
 * Stance pill label for a week-quality string.
 */
export type WeekTeaserStance = "Push" | "Careful" | "Steady" | "Rest";

/**
 * Map a week-quality string onto a compact stance pill.
 *
 * @param quality - Week quality from the bundle (e.g. `Best to push`)
 * @returns Stance label for PdfWeekTile
 */
export function weekStanceFromQuality(quality: string): WeekTeaserStance {
  const raw = typeof quality === "string" ? quality.trim().toLowerCase() : "";
  if (raw.includes("push") || raw.includes("best")) {
    return "Push";
  }
  if (raw.includes("careful") || raw.includes("prepare")) {
    return "Careful";
  }
  if (raw.includes("steady")) {
    return "Steady";
  }
  if (raw.includes("wrap") || raw.includes("rest")) {
    return "Rest";
  }
  return "Steady";
}

/**
 * Canva band label for the alignment score card.
 */
export type AlignmentBandLabel = "LOW ALIGNMENT" | "MID ALIGNMENT" | "HIGH ALIGNMENT";

/**
 * Map a convergence band onto LOW / MID / HIGH ALIGNMENT.
 *
 * @param band - `clash` | `partial` | `aligned`
 * @returns Uppercase band label for the score card
 */
export function alignmentBandLabel(
  band: "aligned" | "partial" | "clash"
): AlignmentBandLabel {
  if (band === "aligned") {
    return "HIGH ALIGNMENT";
  }
  if (band === "partial") {
    return "MID ALIGNMENT";
  }
  return "LOW ALIGNMENT";
}

/**
 * Executive-insight quote for Alignment & Execution (Canva page-05 product chrome).
 * Not derived from convergence summary — that copy stays on the score card.
 */
export const ALIGNMENT_EXECUTIVE_INSIGHT =
  "Alignment is not about pushing harder. It is about timing, clarity, and decisive execution.";

/**
 * Three scannable pillars on the Canva Alignment executive bar (right side).
 */
export const ALIGNMENT_INSIGHT_PILLARS: readonly {
  readonly label: string;
  readonly key: "timely" | "aligned" | "compound";
}[] = [
  { key: "timely", label: "Timely Decisions" },
  { key: "aligned", label: "Aligned Actions" },
  { key: "compound", label: "Compound Results" },
];

/**
 * Executive-insight quote for Alignment & Execution.
 * Uses Canva page-05 product chrome. Live fields are accepted for API
 * compatibility but ignored while the chrome quote is defined.
 *
 * @param _summary - Unused; score-card copy stays on the timing card
 * @param _bandMeaning - Unused while chrome quote is set
 * @param _scoreExplainer - Unused while chrome quote is set
 * @returns Canva executive-insight sentence
 */
export function alignmentInsightQuote(
  _summary: string,
  _bandMeaning: string,
  _scoreExplainer: string
): string {
  return ALIGNMENT_EXECUTIVE_INSIGHT;
}

/**
 * Collect unique non-empty lines from mixed string / string[] pools.
 * Does not invent items — only deduplicates and caps the list.
 *
 * @param pools - Ordered sources (arrays or single strings)
 * @param limit - Maximum rows to return (default 3)
 * @returns Up to `limit` unique trimmed strings
 */
export function collectUniqueLines(
  pools: readonly (readonly string[] | string)[],
  limit = 3
): string[] {
  const cap =
    typeof limit === "number" && Number.isFinite(limit) && limit > 0
      ? Math.trunc(limit)
      : 3;
  const seen = new Set<string>();
  const out: string[] = [];

  for (const pool of pools) {
    const items: string[] = typeof pool === "string" ? [pool] : Array.isArray(pool) ? [...pool] : [];
    for (const item of items) {
      if (typeof item !== "string") {
        continue;
      }
      const trimmed = item.trim();
      if (trimmed.length === 0) {
        continue;
      }
      const key = trimmed.toLowerCase();
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      out.push(trimmed);
      if (out.length >= cap) {
        return out;
      }
    }
  }
  return out;
}

/**
 * Protect This Month banner body from the live scorecard coach line.
 * Dashes are stripped so print copy stays free of `-` / `--` punctuation.
 *
 * @param protectPress - `bundle.scorecardProtectPress`
 * @returns Trimmed body, or empty when the source is blank
 */
export function protectBannerBody(protectPress: string): string {
  const raw = typeof protectPress === "string" ? protectPress.trim() : "";
  if (raw.length === 0) {
    return "";
  }
  return stripPrintDashes(raw);
}

/**
 * Remove hyphen and dash characters from print wording.
 * Collapses leftover double spaces after removal.
 *
 * @param text - Source copy
 * @returns Dash free string
 */
export function stripPrintDashes(text: string): string {
  const raw = typeof text === "string" ? text : "";
  return raw
    .replace(/[\u2010-\u2015\u2212\-–—]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/**
 * Page intro for Four Areas This Month (Canva page-06).
 * Product chrome. No hyphen or dash characters in the wording.
 */
export const SCORECARD_PAGE_INTRO =
  "This page shows how Career, Wealth, Relationships, and Health are scoring this month. Read the bars for prior, current, and next, then use the trend cards to see which areas are rising, easing, or holding steady.";

/** Blurb under Scorecard overview. */
export const SCORECARD_OVERVIEW_BLURB =
  "Your four core life areas with prior, current, and next month readings shown together. Track where pressure is building and where support is opening.";

/** Blurb above the 2×2 trend cards. */
export const SCORECARD_TRENDS_BLURB =
  "Each card names the direction from last month into this one. Rising means the area is gaining force. Easing means intensity is cooling. Steady means the reading holds near last month.";

/** Short coaching line above Protect when live protect text exists. */
export const SCORECARD_PROTECT_INTRO =
  "Use this banner as the month guardrail. Protect the softest area first, then press the strongest when timing allows.";

/**
 * One line decode under a trend status word.
 *
 * @param trend - up | down | flat
 * @returns Short coaching sentence without dashes
 */
export function scorecardTrendCue(trend: "up" | "down" | "flat"): string {
  if (trend === "up") {
    return "Gaining force versus last month.";
  }
  if (trend === "down") {
    return "Cooling versus last month.";
  }
  return "Holding near last month.";
}

/**
 * Canva week-card header label for a quality string.
 *
 * @param quality - Week quality from the bundle (e.g. `Best to push`)
 * @returns Uppercase stance header (`BEST TO PUSH` / `GO CAREFUL` / `STEADY WORK` / `WRAP AND REST`)
 */
export function weekHeaderStanceLabel(quality: string): string {
  const stance = weekStanceFromQuality(quality);
  if (stance === "Push") {
    return "BEST TO PUSH";
  }
  if (stance === "Careful") {
    return "GO CAREFUL";
  }
  if (stance === "Steady") {
    return "STEADY WORK";
  }
  return "WRAP AND REST";
}

/**
 * Weeks-chapter intro line. Uses the live profile name when present.
 *
 * @param name - Profile display name
 * @returns One intro sentence
 */
export function weekCalendarIntro(name: string): string {
  const n = typeof name === "string" ? name.trim() : "";
  if (n.length > 0) {
    return `${n}, each week has a different pace. Match the header stance, then use the FOCUS line.`;
  }
  return "Each week has a different pace. Match the header stance, then use the FOCUS line.";
}

/**
 * Product-chrome tip for the Weeks calendar (not client sample copy).
 */
export const WEEK_CALENDAR_TIP =
  "Small consistent actions create long-term transformation. Focus on progress, not perfection.";

/**
 * Three weekly check-in questions from live contract / trap / palace fields.
 * Pads with product-chrome lines when a source is blank. Does not invent
 * client-specific sample sentences.
 *
 * @param input - Live primary move, trap name, and palace label
 * @returns Exactly three questions
 */
export function weekCheckInQuestions(input: {
  primaryMove: string;
  trapName: string;
  palaceName: string;
}): string[] {
  const questions: string[] = [];
  const moveRaw =
    typeof input.primaryMove === "string" ? input.primaryMove.trim() : "";
  const move = firstClauseHeading(moveRaw, 8);
  if (move.length > 0) {
    questions.push(`Did I take this week's move: ${move}?`);
  }
  const trap = typeof input.trapName === "string" ? input.trapName.trim() : "";
  if (trap.length > 0) {
    questions.push(`Did the trap (${trap}) run any day this week?`);
  }
  const palace = stripPalaceSuffix(
    typeof input.palaceName === "string" ? input.palaceName : ""
  );
  if (palace.length > 0) {
    questions.push(
      `What one ${palace} action will I close before next week?`
    );
  }
  const pad = [
    "What did I protect this week, and what did I postpone?",
    "Where did I follow the FOCUS line, and where did I drift?",
    "What will I do first when the next week opens?",
  ];
  for (const line of pad) {
    if (questions.length >= 3) {
      break;
    }
    questions.push(line);
  }
  return questions.slice(0, 3);
}

/**
 * Nobleman cue subtitle: palace plus animal energy, from live data only.
 *
 * @param palaceName - English palace name
 * @param zodiac - English zodiac / animal name
 * @returns Cue line, or empty when both sources are blank
 */
export function noblemanCueLine(palaceName: string, zodiac: string): string {
  const palace = typeof palaceName === "string" ? palaceName.trim() : "";
  const animal = typeof zodiac === "string" ? zodiac.trim() : "";
  if (palace.length > 0 && animal.length > 0) {
    return `${palace}. Look for ${animal} energy.`;
  }
  if (palace.length > 0) {
    return palace;
  }
  if (animal.length > 0) {
    return `Look for ${animal} energy.`;
  }
  return "";
}

/**
 * Short tag pills for a nobleman card, derived from the first characteristics
 * clause and star names. Does not invent labels.
 *
 * @param characteristics - Profile characteristics sentence
 * @param stars - Comma-separated star names
 * @returns Up to three short tags
 */
export function noblemanTagPills(
  characteristics: string,
  stars: string
): string[] {
  const tags: string[] = [];
  const seen = new Set<string>();
  const pushTag = (value: string): void => {
    const trimmed = value.trim().replace(/^or\s+/i, "").replace(/[.;]+$/g, "");
    if (trimmed.length === 0 || trimmed.length > 28) {
      return;
    }
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    tags.push(trimmed);
  };

  const chars =
    typeof characteristics === "string" ? characteristics.trim() : "";
  const firstClause = chars.split(/[.;]/)[0];
  const clause =
    typeof firstClause === "string" && firstClause.trim().length > 0
      ? firstClause
      : "";
  if (clause.length > 0) {
    const parts = clause.split(/,/);
    for (const part of parts) {
      pushTag(part);
      if (tags.length >= 3) {
        return tags;
      }
    }
  }

  const starRaw = typeof stars === "string" ? stars.trim() : "";
  if (starRaw.length > 0) {
    const parts = starRaw.split(/,/);
    for (const part of parts) {
      pushTag(part);
      if (tags.length >= 3) {
        return tags;
      }
    }
  }
  return tags;
}

/**
 * Year-map timeline status for a lunar month relative to the current month.
 */
export type YearTimelineStatus = "PAST" | "CURRENT" | "AHEAD";

/**
 * Map a year-climate row onto PAST / CURRENT / AHEAD.
 *
 * @param isCurrent - Whether the row is this report's month
 * @param lunarMonth - Row lunar month (1–12)
 * @param currentMonth - Current lunar month (1–12)
 * @returns Timeline status label
 */
export function yearTimelineStatus(
  isCurrent: boolean,
  lunarMonth: number,
  currentMonth: number
): YearTimelineStatus {
  if (isCurrent === true) {
    return "CURRENT";
  }
  const month =
    typeof lunarMonth === "number" && Number.isFinite(lunarMonth)
      ? Math.trunc(lunarMonth)
      : 0;
  const now =
    typeof currentMonth === "number" && Number.isFinite(currentMonth)
      ? Math.trunc(currentMonth)
      : 0;
  if (month < now) {
    return "PAST";
  }
  return "AHEAD";
}

/**
 * Letter-page intro: live greeting name plus closing or first body paragraph.
 * Does not invent client sample sentences.
 *
 * @param name - Profile display name
 * @param closing - Letter closing line
 * @param firstBody - First letter body paragraph
 * @returns Intro sentence, or empty when all sources are blank
 */
export function letterCloseIntro(
  name: string,
  closing: string,
  firstBody: string
): string {
  const n = typeof name === "string" ? name.trim() : "";
  const close = typeof closing === "string" ? closing.trim() : "";
  const body = typeof firstBody === "string" ? firstBody.trim() : "";
  const prose = close.length > 0 ? close : body;
  if (n.length > 0 && prose.length > 0) {
    return `${n}, ${prose}`;
  }
  if (n.length > 0) {
    return `${n}, this month's close is below.`;
  }
  return prose;
}
